import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { VISIT_TYPE_CONFIG } from '../../config/visitTypeFields';
import { registerVisitor, lookupVisitor } from '../../services/visitorService';
import { getPublishedEvents } from '../../services/eventService';
import NdaBlock from '../../components/form/NdaBlock';
import '../../styles/RegisterForm.css';

export default function RegisterForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedEvent = searchParams.get('event');

  const { register, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = useForm({
    defaultValues: {
      visitType: preselectedEvent ? 'evenement' : '',
      eventId: preselectedEvent || '',
      gdprConsent: false,
    }
  });

  const [events, setEvents] = useState([]);
  const [step, setStep] = useState('form'); // 'form' | 'nda' | 'submitting'
  const [ndaData, setNdaData] = useState(null);
  const [isReturning, setIsReturning] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState(null);

  const visitType = watch('visitType');
  const isStudent = watch('isStudent');
  const typeConfig = visitType ? VISIT_TYPE_CONFIG[visitType] : null;

  // Load published events
  useEffect(() => {
    getPublishedEvents()
      .then(res => {
        if (res.success) setEvents(res.data);
      })
      .catch(() => {});
  }, []);

  // Returning visitor lookup (debounced)
  const handleEmailBlur = useCallback(async (e) => {
    const email = e.target.value;
    if (!email || !email.includes('@')) return;

    try {
      const res = await lookupVisitor(email);
      if (res.success && res.data) {
        setValue('fullName', res.data.fullName);
        setValue('phone', res.data.phone);
        setIsReturning(true);
      }
    } catch {
      setIsReturning(false);
    }
  }, [setValue]);

  const onFormSubmit = (data) => {
    setError('');
    const needsNda = typeConfig?.ndaRequired ||
      (visitType === 'evenement' && events.find(e => e.id === data.eventId)?.ndaRequired);

    if (needsNda && !ndaData) {
      setFormData(data);
      setStep('nda');
      return;
    }

    submitRegistration(data, ndaData);
  };

  const submitRegistration = async (data, nda) => {
    setStep('submitting');
    try {
      const payload = {
        ...data,
        nda: nda || null,
      };

      const res = await registerVisitor(payload);
      if (res.success) {
        navigate('/confirmation', {
          state: {
            qrPayload: res.data.qrPayload,
            visitorName: res.data.visitorName,
            visitType: res.data.visitType,
            expiresAt: res.data.expiresAt,
          }
        });
      }
    } catch (err) {
      const msg = err.response?.data?.error?.message || 'Une erreur est survenue';
      setError(msg);
      setStep('form');
    }
  };

  const handleNdaAccept = (nda) => {
    setNdaData(nda);
    submitRegistration(formData, nda);
  };

  if (step === 'nda') {
    return (
      <NdaBlock
        onAccept={handleNdaAccept}
        onDecline={() => setStep('form')}
      />
    );
  }

  const showField = (fieldName) => typeConfig?.fields?.includes(fieldName);
  // university/fieldOfStudy shown if student checkbox is checked OR visit type includes them
  const showStudentFields = isStudent || showField('university');

  return (
    <div className="register-page">
      <div className="register-container">
        <div className="register-header">
          <img src="/images/logo.png" alt="Startup Village" className="register-logo" />
          <h1>Bienvenue à Startup Village</h1>
          <p>Remplissez le formulaire pour obtenir votre pass d'accès QR</p>
          {isReturning && <span className="returning-badge">Bon retour !</span>}
        </div>

        {error && <div className="error-banner">{error}</div>}

        <form onSubmit={handleSubmit(onFormSubmit)} className="register-form">
          {/* Always-present fields */}
          <div className="form-group">
            <label>Nom complet *</label>
            <input
              {...register('fullName', { required: 'Le nom est requis', minLength: { value: 2, message: 'Minimum 2 caractères' } })}
              placeholder="Votre nom complet"
            />
            {errors.fullName && <span className="field-error">{errors.fullName.message}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                {...register('email', { required: 'L\'email est requis', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Format email invalide' } })}
                placeholder="votre@email.com"
                onBlur={handleEmailBlur}
              />
              {errors.email && <span className="field-error">{errors.email.message}</span>}
            </div>

            <div className="form-group">
              <label>Téléphone *</label>
              <input
                type="tel"
                {...register('phone', { required: 'Le téléphone est requis', pattern: { value: /^\+?[0-9]{8,15}$/, message: '8-15 chiffres' } })}
                placeholder="+216 XX XXX XXX"
              />
              {errors.phone && <span className="field-error">{errors.phone.message}</span>}
            </div>
          </div>

          {/* Universal student checkbox — always visible */}
          <div className="form-group consent-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                {...register('isStudent')}
              />
              {' '}<span>Je suis étudiant(e)</span>
            </label>
          </div>

          {/* Student fields — appear right under checkbox when checked */}
          {showStudentFields && (
            <>
              <div className="form-group">
                <label>Université / École</label>
                <input {...register('university')} placeholder="Nom de l'université" />
              </div>
              <div className="form-group">
                <label>Filière d'études</label>
                <input {...register('fieldOfStudy')} placeholder="Votre filière" />
              </div>
            </>
          )}

          <div className="form-group">
            <label>Type de demande *</label>
            <select {...register('visitType', { required: 'Veuillez sélectionner un type' })}>
              <option value="">— Sélectionnez —</option>
              {Object.entries(VISIT_TYPE_CONFIG)
                .filter(([key]) => !(isStudent && key === 'fournisseur'))
                .map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
            </select>
            {errors.visitType && <span className="field-error">{errors.visitType.message}</span>}
          </div>

          {showField('eventId') && (
            <div className="form-group">
              <label>Événement *</label>
              <select {...register('eventId', { required: visitType === 'evenement' ? 'Veuillez sélectionner un événement' : false })}>
                <option value="">— Sélectionnez un événement —</option>
                {events.map(event => (
                  <option
                    key={event.id}
                    value={event.id}
                    disabled={event.isFull}
                  >
                    {event.title} — {new Date(event.eventDate).toLocaleDateString('fr-FR')}
                    {event.isFull ? ' (Complet)' : ''}
                  </option>
                ))}
              </select>
              {errors.eventId && <span className="field-error">{errors.eventId.message}</span>}
            </div>
          )}

          {showField('hostCompany') && (
            <div className="form-group">
              <label>Entreprise hôte *</label>
              <input {...register('hostCompany', { required: 'Requis' })} placeholder="Nom de l'entreprise" />
              {errors.hostCompany && <span className="field-error">{errors.hostCompany.message}</span>}
            </div>
          )}

          {showField('hostContactName') && (
            <div className="form-group">
              <label>Personne à rencontrer *</label>
              <input {...register('hostContactName', { required: 'Requis' })} placeholder="Nom du contact" />
              {errors.hostContactName && <span className="field-error">{errors.hostContactName.message}</span>}
            </div>
          )}

          {showField('hostPersonName') && (
            <div className="form-group">
              <label>Personne à visiter *</label>
              <input {...register('hostPersonName', { required: 'Requis' })} placeholder="Nom de la personne" />
              {errors.hostPersonName && <span className="field-error">{errors.hostPersonName.message}</span>}
            </div>
          )}

          {showField('supplierCompany') && (
            <div className="form-group">
              <label>Société fournisseur *</label>
              <input {...register('supplierCompany', { required: 'Requis' })} placeholder="Nom de la société" />
              {errors.supplierCompany && <span className="field-error">{errors.supplierCompany.message}</span>}
            </div>
          )}

          {showField('svContact') && (
            <div className="form-group">
              <label>Contact SV *</label>
              <input {...register('svContact', { required: 'Requis' })} placeholder="Contact à Startup Village" />
              {errors.svContact && <span className="field-error">{errors.svContact.message}</span>}
            </div>
          )}

          {showField('targetCompany') && (
            <div className="form-group">
              <label>Entreprise à visiter *</label>
              <input {...register('targetCompany', { required: 'Requis' })} placeholder="Nom de l'entreprise" />
              {errors.targetCompany && <span className="field-error">{errors.targetCompany.message}</span>}
            </div>
          )}

          {showField('visitPurpose') && (
            <div className="form-group">
              <label>Objet de la visite *</label>
              <textarea {...register('visitPurpose', { required: 'Requis' })} placeholder="Décrivez brièvement l'objet de votre visite" rows={3} />
              {errors.visitPurpose && <span className="field-error">{errors.visitPurpose.message}</span>}
            </div>
          )}

          {/* Commentaire */}
          <div className="form-group">
            <label>Commentaire (optionnel)</label>
            <textarea
              {...register('comments')}
              placeholder="Informations supplémentaires..."
              rows={3}
            />
          </div>

          {/* GDPR consent */}
          <div className="form-group consent-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                {...register('gdprConsent', { required: 'Vous devez accepter la politique de confidentialité' })}
              />
              {' '}J'accepte la collecte et le traitement de mes données conformément à la politique de confidentialité de Startup Village *
            </label>
            {errors.gdprConsent && <span className="field-error">{errors.gdprConsent.message}</span>}
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-submit"
            disabled={isSubmitting || step === 'submitting'}
          >
            {isSubmitting || step === 'submitting' ? 'Envoi en cours...' : 'Obtenir mon pass QR'}
          </button>
        </form>
      </div>
    </div>
  );
}
